import React, { useState, useEffect } from 'react';
import {
  TextField,
  Button,
  Box,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  FormHelperText,
  Checkbox,
  ListItemText,
  OutlinedInput,
  CircularProgress,
  Typography,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import { fget, fpost } from '../../axios/axiosCalls';
import toast from 'react-hot-toast';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { Country } from 'country-state-city';
import PhoneInputCustom from '../PhoneInputCustom';

const steps = ['Contact Details', 'Email', 'Personal Information', 'University Details', 'Guarantor Details'];

const AddCustomerForm = ({onSuccess}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [countryOptions, setCountryOptions] = useState([]);
  const [countryAllOptions, setCountryAllOptions] = useState([]);
  const [isValidating, setIsValidating] = useState(false);
  const [stateOptions, setStateOptions] = useState([]);

  useEffect(() => {
    const countries = Country.getAllCountries().map(country => ({
      name: country.name,
      code: country.isoCode,
    }));
    countries.sort((a, b) => a.name.localeCompare(b.name));
    setCountryOptions(countries);
  }, []);

  const handlePhoneChange = (value) => {
    setFormData(prev => ({
      ...prev,
      phone: value
    }));
  };
  const [formData, setFormData] = useState({
    phone: '',
    countryCode: '',
    email: '',
    name: '',
    dob: '',
    gender: '',
    nationality: '',
    state: '',
    city: '',
    languages: [],
    university: '',
    campus: '',
    toCountry: '',
    course: '',
    intake: '',
    intakeYear: '',
    mothertongue: '',
    resCountry: '',
    guarantorType: '',
    guarantorName: '',
    guarantorEmail: '',
    guarantorPhone: '',
    guarantorCountry: ''
  });

  const [errors, setErrors] = useState({});
  const [loadingCountries, setLoadingCountries] = useState(true);
  const [universities, setUniversities] = useState([]);
  const languageOptions = ['English', 'Spanish', 'French', 'German', 'Mandarin'];
  const genderOptions = ['Male', 'Female', 'Other'];
  const intakeOptions = ['January','February','March','April','May','June', 'July', 'August', 'September','October','November','December'];
  const mothertongueOptions = ['Malayalam', 'Hindi', 'English', 'Tamil', 'Telugu'];
  const [campuses, setCampuses] = useState([]);
  const [loadingCampuses, setLoadingCampuses] = useState(false);
  
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await fget({ url: '/api/v2/countries' });
        // Axios responses have the data in 'data' property
        if (response.status === 200) {
          const countries = response.data.countries.map(country => ({
            name: country.cname,
            id: country.id,
          }));
          // Sort countries alphabetically
          countries.sort((a, b) => a.name.localeCompare(b.name));
          setCountryOptions(countries);
        } else {
          throw new Error(`Error: ${response.status}`);
        }
      } catch (error) {
        toast.error('Failed to fetch countries.');
        console.error('Fetch Countries Error:', error);
      } finally {
        setLoadingCountries(false);
      }
    };

    fetchCountries();
  }, []);

  useEffect(() => {
    const fetchAllCountries = async () => {
      try {
        const response = await fget({ url: '/api/v2/countries?all=1' });
        // Axios responses have the data in 'data' property
        if (response.status === 200) {
          const countries = response.data.data.map((country,index )=> ({
            name: country.cname,
            id: index,
            states: country.states || [], // Extract states,
            nationality: country.nationality
          }));
          // Sort countries alphabetically
          countries.sort((a, b) => a.name.localeCompare(b.name));
          setCountryAllOptions(countries);
        } else {
          throw new Error(`Error: ${response.status}`);
        }
      } catch (error) {
        toast.error('Failed to fetch countries.');
        console.error('Fetch Countries Error:', error);
      } finally {
        setLoadingCountries(false);
      }
    };

    fetchAllCountries();
  }, []);

  const fetchUniversities = async () => {
    try {
      const response = await fget({ url: `/api/v2/colleges?countryId=${formData.toCountry}` });
      if (response.data.status === 'Success') {
        setUniversities(response.data.colleges);
      } else {
        throw new Error('Failed to fetch universities');
      }
    } catch (error) {
      toast.error('Failed to fetch universities');
      console.error('Fetch Universities Error:', error);
    }
  }
  useEffect(() => {
    if (formData.toCountry!=='') {
      fetchUniversities();
    }
  }, [formData.toCountry]);
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

  };

  const handleLanguagesChange = (event) => {
    const {
      target: { value },
    } = event;
    setFormData({
      ...formData,
      languages: typeof value === 'string' ? value.split(',') : value,
    });
  };
  const checkEmailExistsSync = async (email) => {
    if (!email) return false;
    
    try {
      const response = await fget({
        url: `/api/v2/is_registered`,
        params: { email }
      });
      return response.data.status === 'Success'? false : true;
    } catch (error) {
      console.error('Email check error:', error);
      return true;
    }
  };
  const validateStep = async() => {
    setIsValidating(true);

    let temp = {};
    try{
    if (activeStep === 0) {
      temp.phone = formData.phone.length >= 6 ? '' : 'Minimum 6 characters required.';
      // temp.countryCode = formData.countryCode ? '' : 'This field is required.';
    } else if (activeStep === 1) {
      if (!formData.email) {
        temp.email = 'Email is required';
      } else {
        const exists = await checkEmailExistsSync(formData.email);
        if (exists) {
          temp.email = 'This email is already registered';
        }
      }
    } else if (activeStep === 2) {
      temp.name = formData.name ? '' : 'This field is required.';
      temp.dob = formData.dob ? '' : 'This field is required.';
      temp.gender = formData.gender ? '' : 'This field is required.';
      temp.nationality = formData.nationality ? '' : 'This field is required.';
      temp.state = formData.state ? '' : 'This field is required.';
      temp.city = formData.city ? '' : 'This field is required.';
      temp.languages = formData.languages.length > 0 ? '' : 'At least one language must be selected.';
      temp.mothertongue = formData.mothertongue ? '' : 'This field is required.';
    } else if (activeStep === 3) {
      temp.toCountry = formData.toCountry ? '' : 'This field is required.';
      temp.university = formData.university ? '' : 'This field is required.';
      temp.campus = formData.campus ? '' : 'This field is required.';
      temp.course = formData.course ? '' : 'This field is required.';
      temp.intake = formData.intake ? '' : 'This field is required.';
      temp.intakeYear = /^[0-9]{4}$/.test(formData.intakeYear) ? '' : 'Enter a valid year.';
    } else if (activeStep === 4) {
      // temp.guarantorName = formData.guarantorName ? '' : 'Guarantor name is required';
      // temp.guarantorEmail = formData.guarantorEmail ? '' : 'Guarantor email is required';
      // temp.guarantorPhone = formData.guarantorPhone ? '' : 'Guarantor phone is required';
      // temp.guarantorCountry = formData.guarantorCountry ? '' : 'Guarantor country is required';
    }
    }
    finally{
      setIsValidating(false);
    }
    setErrors({
      ...temp,
    });
    debugger
    return Object.values(temp).every((x) => x === '');
  };
  const fetchCampuses = async () => {
    if (!formData.university) {
      setCampuses([]);
      return;
    }
    setLoadingCampuses(true);
    try {
      const response = await fget({ 
        url: '/api/v2/campuses',
        params: {
          collegeId: formData.university,
          countryId: formData.toCountry
        }
      });
      if (response.data.status === 'Success') {
        setCampuses(response.data.campuses);
      }
    } catch (error) {
      toast.error('Failed to fetch campuses');
      console.error('Fetch Campuses Error:', error);
    } finally {
      setLoadingCampuses(false);
    }
  };
  useEffect(() => {
    if (formData.university) {
      fetchCampuses();
    }
  }, [formData.university]);
  const handleNext = async () => {
    const isValid = await validateStep();
    if (isValid) {
      setActiveStep((prev) => prev + 1);
    } else {
      if (activeStep!==1){
        toast.error('Please fill the required fields correctly.');
      }
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };
  useEffect(() => {
    if (formData.resCountry) {
      const country = countryAllOptions.find(c => c.name === formData.resCountry);
      if (country) {
        setStateOptions(country.states || []);
        // Reset state selection when country changes
        setFormData(prev => ({...prev, state: ''}));
      }
    }
  }, [formData.resCountry]);
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateStep()) {
      try {
        const payload = {
          ...formData,
          resCountry: formData.resCountry,
          college: formData.university
            ? universities.find(u => u.collegeId === formData.university)?.name
            : '',
          type: 'lead',
          campus: formData.campus
            ? campuses.find(c => c.campusId === formData.campus)?.name
            : '',
        };
  
        // Only add guarantor if a type is selected
        if (formData.guarantorType!=='') {
          payload.guarantor = {
            type: formData.guarantorType,
            name: formData.guarantorName,
            email: formData.guarantorEmail,
            phone: formData.guarantorPhone,
            country: formData.guarantorCountry,
          };
        }
  
        const response = await fpost({
          url: '/api/v2/channelpartner/students',
          data: payload,
        });
  
        if (response.data.status === 'Success') {
          toast.success('Customer added successfully!');
          setFormData({
            phone: '',
            countryCode: '',
            email: '',
            name: '',
            dob: '',
            gender: '',
            nationality: '',
            state: '',
            city: '',
            languages: [],
            university: '',
            campus: '',
            course: '',
            intake: '',
            toCountry: '',
            intakeYear: '',
            mothertongue: '',
            resCountry: '',
            guarantorType: '',
            guarantorName: '',
            guarantorEmail: '',
            guarantorPhone: '',
            guarantorCountry: ''
          });
          setActiveStep(0);
          setErrors({});
          onSuccess(response?.data?.student)
        } else {
          
          toast.error('Failed to add customer.');
        }
      } catch (error) {
        toast.error(Object.values(error?.response?.data?.errors || {})[0] || 
        error?.response?.data?.message || 'An error occurred while adding the customer.')
        console.error('Add Customer Error:', error);
      }
    } else {
      toast.error('Please fill the required fields correctly.');
    }
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
            <Box sx={{ mb: 2, mt:2 }}>
              <PhoneInputCustom
                      value={formData.phone}
                      onChange={handlePhoneChange}
                      size='small'
                      style={{
                        width: '100%'
                      }}
                      error={Boolean(errors.phone)}
                    />
              
              {errors.phone && (
                <FormHelperText error>{errors.phone}</FormHelperText>
              )}
            </Box>
          );
      case 1:
        return (
          <TextField
            required
            label="Email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            error={Boolean(errors.email)}
            helperText={errors.email}
            fullWidth
            margin="normal"
          />
        );
      case 2:
        return (
          <>
            <TextField
              required
              label="Name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              error={Boolean(errors.name)}
              helperText={errors.name}
              fullWidth
              margin="normal"
            />
            <TextField
              required
              label="Date of Birth"
              name="dob"
              type="date"
              InputLabelProps={{
                shrink: true,
              }}
              value={formData.dob}
              onChange={handleChange}
              error={Boolean(errors.dob)}
              helperText={errors.dob}
              fullWidth
              margin="normal"
            />
            <FormControl required error={Boolean(errors.gender)} fullWidth margin="normal">
              <InputLabel>Gender</InputLabel>
              <Select
                name="gender"
                value={formData.gender}
                onChange={handleChange}
                input={<OutlinedInput label="Gender" />}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {genderOptions.map((gender) => (
                  <MenuItem key={gender} value={gender.toLowerCase()}>
                    {gender}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>{errors.gender}</FormHelperText>
            </FormControl>
            <FormControl fullWidth margin="normal">
                <InputLabel id="country-label">Country of Residence*</InputLabel>
                <Select
                  labelId="country-label"
                  name="resCountry"
                  value={formData.resCountry || ''}
                  onChange={handleChange}
                  input={<OutlinedInput label="Country" />}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {countryAllOptions.map((country) => (
                    <MenuItem key={country.id} value={country.name}>
                      {country.name}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>{errors.country}</FormHelperText>
              </FormControl>

            <FormControl required error={Boolean(errors.state)} fullWidth margin="normal">
              <InputLabel>State of Residence</InputLabel>
              <Select
                name="state"
                value={formData.state}
                onChange={handleChange}
                input={<OutlinedInput label="State of Residence" />}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {stateOptions.map((state, index) => (
                  <MenuItem key={index} value={state.name}>
                    {state.name}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>{errors.state}</FormHelperText>
            </FormControl>
            <TextField
              required
              label="City of Residence"
              name="city"
              value={formData.city}
              onChange={handleChange}
              error={Boolean(errors.city)}
              helperText={errors.city}
              fullWidth
              margin="normal"
            />
                          <FormControl fullWidth margin="normal">
                <InputLabel id="nationality-label">Nationality*</InputLabel>
                <Select
                  labelId="nationality-label"
                  name="nationality"
                  value={formData.nationality || ''}
                  onChange={handleChange}
                  input={<OutlinedInput label="Nationality" />}
                  error={Boolean(errors.nationality)}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {countryAllOptions.map((country) => (
                    <MenuItem key={country.id} value={country.nationality}>
                      {country.nationality}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>{errors.nationality}</FormHelperText>
              </FormControl>
            <FormControl required error={Boolean(errors.languages)} fullWidth margin="normal">
              <InputLabel>Languages</InputLabel>
              <Select
                multiple
                name="languages"
                value={formData.languages}
                onChange={handleLanguagesChange}
                input={<OutlinedInput label="Languages" />}
                renderValue={(selected) => selected.join(', ')}
              >
                {languageOptions.map((language) => (
                  <MenuItem key={language} value={language}>
                    <Checkbox checked={formData.languages.indexOf(language) > -1} />
                    <ListItemText primary={language} />
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>{errors.languages}</FormHelperText>
            </FormControl>
            <FormControl required error={Boolean(errors.mothertongue)} fullWidth margin="normal">
              <InputLabel>Mothertongue</InputLabel>
              <Select
                name="mothertongue"
                value={formData.mothertongue}
                onChange={handleChange}
                input={<OutlinedInput label="Mothertongue" />}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {mothertongueOptions.map((tongue) => (
                  <MenuItem key={tongue} value={tongue}>
                    {tongue}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>{errors.mothertongue}</FormHelperText>
            </FormControl>
          </>
        );
      case 3:
        return (
          <>
          <FormControl fullWidth error={Boolean(errors.toCountry)} sx={{mt:2}}>
            <InputLabel>Select Country</InputLabel>
            <Select
              value={formData.toCountry}
              onChange={handleChange}
              name="toCountry"
              label="Select Country"
            >
              <MenuItem value="">Select Countries</MenuItem>
              {countryOptions.map((country) => (
                <MenuItem key={country.id} value={country.id}>
                  {country.name}
                </MenuItem>
              ))}
            </Select>
            {errors.toCountry && <FormHelperText>{errors.toCountry}</FormHelperText>}
          </FormControl>

          <FormControl fullWidth error={Boolean(errors.university)} sx={{mt:2}}>
            <InputLabel>Select University</InputLabel>
            <Select
              required
              value={formData.university}
              onChange={handleChange}
              name="university"
              label="Select University"
            >
              <MenuItem value="">Select University</MenuItem>
              {universities.map((univ) => (
                <MenuItem key={univ.collegeId} value={univ.collegeId}>
                  {univ.name}
                </MenuItem>
              ))}
            </Select>
            {errors.university && <FormHelperText>{errors.university}</FormHelperText>}
          </FormControl>
          <FormControl fullWidth error={Boolean(errors.campus)} sx={{mt:2}}>
            <InputLabel>Select Campus</InputLabel>
            <Select
              required
              value={formData.campus}
              onChange={handleChange}
              name="campus"
              label="Select Campus"
              disabled={loadingCampuses}
            >
              <MenuItem value="">Select Campus</MenuItem>
              {campuses.map((campus) => (
                <MenuItem key={campus.campusId} value={campus.campusId}>
                  {campus.name}
                </MenuItem>
              ))}
            </Select>
            {errors.campus && <FormHelperText>{errors.campus}</FormHelperText>}
          </FormControl>
            <TextField
              required
              label="Course"
              name="course"
              value={formData.course}
              onChange={handleChange}
              error={Boolean(errors.course)}
              helperText={errors.course}
              fullWidth
              margin="normal"
            />
            <FormControl required error={Boolean(errors.intake)} fullWidth margin="normal">
              <InputLabel>Intake</InputLabel>
              <Select
                name="intake"
                value={formData.intake}
                onChange={handleChange}
                input={<OutlinedInput label="Intake" />}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {intakeOptions.map((intake) => (
                  <MenuItem key={intake} value={intake}>
                    {intake}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>{errors.intake}</FormHelperText>
            </FormControl>
            <TextField
              required
              label="Intake Year"
              name="intakeYear"
              type="number"
              value={formData.intakeYear}
              onChange={handleChange}
              error={Boolean(errors.intakeYear)}
              helperText={errors.intakeYear}
              fullWidth
              margin="normal"
            />

          </>
        );
      case 4:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Guarantor Type</InputLabel>
              <Select
                name="guarantorType"
                value={formData.guarantorType}
                onChange={handleChange}
                label="Guarantor Type"
              >
                <MenuItem value="local">Local</MenuItem>
                <MenuItem value="international">International</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Guarantor Name"
              name="guarantorName"
              fullWidth
              // required
              value={formData.guarantorName}
              onChange={handleChange}
              error={Boolean(errors.guarantorName)}
              helperText={errors.guarantorName}
            />

            <TextField
              label="Guarantor Email"
              name="guarantorEmail"
              type="email"
              fullWidth
              // required
              value={formData.guarantorEmail}
              onChange={handleChange}
              error={Boolean(errors.guarantorEmail)}
              helperText={errors.guarantorEmail}
            />

            <TextField
              label="Guarantor Phone"
              name="guarantorPhone"
              fullWidth
              // required
              value={formData.guarantorPhone}
              onChange={handleChange}
              error={Boolean(errors.guarantorPhone)}
              helperText={errors.guarantorPhone}
            />

              {formData.guarantorType === "international" && (
                <FormControl fullWidth>
                  <InputLabel>Guarantor Country</InputLabel>
                  <Select
                    name="guarantorCountry"
                    value={formData.guarantorCountry}
                    onChange={handleChange}
                    label="Guarantor Country"
                    error={Boolean(errors.guarantorCountry)}
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {countryAllOptions.map((country) => (
                      <MenuItem key={country.id} value={country.name}>
                        {country.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.guarantorCountry && (
                    <FormHelperText error>{errors.guarantorCountry}</FormHelperText>
                  )}
                </FormControl>
              )}
          </Box>
        );
      default:
        return 'Unknown Step';
    }
  };

  return (
    <Box
  sx={{
    maxWidth: { xs: '100%', sm: 600 },
    margin: '0 auto',
    padding: { xs: 1, sm: 2 },
  }}
    >
      <Stepper 
          activeStep={activeStep} 
          alternativeLabel
          sx={{
            '& .MuiStepLabel-label': {
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
            },
            mb: { xs: 2, sm: 3 }
          }}
      >
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>
      <Box     component="form" 
    onSubmit={handleSubmit} 
    noValidate 
    autoComplete="off"
    sx={{
      '& .MuiTextField-root': {
        my: { xs: 1, sm: 2 }
      },
      '& .MuiFormControl-root': {
        my: { xs: 1, sm: 2 }
      }
    }}>
        {renderStepContent(activeStep)}
        <Box 
      mt={2} 
      display="flex" 
      justifyContent="space-between"
      flexDirection={{ xs: 'column', sm: 'row' }}
      gap={{ xs: 1, sm: 2 }}
    >
      {activeStep !== 0 && (
        <Button 
          variant="contained" 
          color="secondary" 
          onClick={handleBack}
          fullWidth={{ xs: true, sm: false }}
          sx={{ height: { xs: 44, sm: 36 } }}
        >
          Back
        </Button>
      )}
      {activeStep < steps.length - 1 && (
        <Button 
          variant="contained" 
          color="primary" 
          onClick={handleNext}
          disabled={isValidating}
          fullWidth={{ xs: true, sm: false }}
          sx={{ height: { xs: 44, sm: 36 } }}
        >
          {isValidating ? <CircularProgress size={24} /> : 'Next'}
        </Button>
      )}
      {activeStep === steps.length - 1 && (
        <Button 
          variant="contained" 
          color="primary" 
          type="submit"
          fullWidth={{ xs: true, sm: false }}
          sx={{ height: { xs: 44, sm: 36 } }}
        >
          Submit
        </Button>
      )}
    </Box>
      </Box>
    </Box>
  );
};

export default AddCustomerForm;